import React from 'react';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import { Button } from './ui/Button';
import Badge from './ui/Badge';
import { useT } from '../i18n';

const T = {
  es: {
    badge: 'PLATAFORMA CADENIA',
    titleTop: 'Visibilidad e',
    titleHighlight: 'Inteligencia',
    subtitle: 'en la Cadena de Suministro',
    description: 'Centralice la información de todos sus transportistas, analice el rendimiento de su red logística y digitalice sus documentos de transporte. Una plataforma, tres productos.',
    ctaDemo: 'Solicitar una demo',
    ctaProducts: 'Ver Productos',
    capabilities: [
      'Trazabilidad de envíos',
      'Seguimiento en Tiempo Real',
      'Supply Chain Analytics',
      'Agentes de IA',
      'Documentos Digitales'
    ],
    metrics: [
      { value: '2M+', label: 'Envíos gestionados' },
      { value: '50+', label: 'Transportistas conectados por cliente' },
      { value: '4', label: 'Países con operaciones activas' },
      { value: '99.9%', label: 'Disponibilidad SLA' }
    ],
  },
  en: {
    badge: 'CADENIA PLATFORM',
    titleTop: 'Visibility and',
    titleHighlight: 'Intelligence',
    subtitle: 'across the Supply Chain',
    description: 'Centralize data from all your carriers, analyze the performance of your logistics network and digitize your transport documents. One platform, three products.',
    ctaDemo: 'Request a demo',
    ctaProducts: 'View Products',
    capabilities: [
      'Shipment Traceability',
      'Real-Time Tracking',
      'Supply Chain Analytics',
      'AI Agents',
      'Digital Documents'
    ],
    metrics: [
      { value: '2M+', label: 'Shipments managed' },
      { value: '50+', label: 'Carriers connected per client' },
      { value: '4', label: 'Countries with active operations' },
      { value: '99.9%', label: 'SLA uptime' }
    ],
  },
};

const Hero = () => {
  const t = useT(T);
  return (
    <section className="relative overflow-visible pt-32 pb-32 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.08),transparent_50%)] animate-pulse" style={{animationDuration: '8s'}} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(16,52,236,0.06),transparent_50%)] animate-pulse" style={{animationDuration: '12s', animationDelay: '4s'}} />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent animate-pulse" style={{animationDuration: '6s'}} />

      <Container>
        <div className="relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-sm font-medium text-blue-700 mb-8">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              {t.badge}
            </div>

            <div className="mb-8">
              <h1 className="text-5xl md:text-7xl font-light text-slate-900 leading-loose" style={{lineHeight: '1.3', paddingBottom: '0.5rem'}}>
                {t.titleTop}
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent font-medium">
                  {t.titleHighlight}
                </span>
              </h1>
            </div>

            <p className="text-xl md:text-2xl text-slate-600 font-light mb-4">
              {t.subtitle}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-16">
            {t.capabilities.map(cap => (
              <div key={cap} className="px-4 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full text-sm font-medium text-slate-700 hover:bg-white hover:shadow-sm transition-all duration-200">
                {cap}
              </div>
            ))}
          </div>

          <div className="text-center mb-20">
            <p className="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed mb-12">
              {t.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button as="a" href="#contacto" size="lg" className="px-12 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200">
                {t.ctaDemo}
              </Button>
              <Button as="a" href="#productos" variant="outline" size="lg" className="px-12 py-4 border-2 hover:bg-slate-50">
                {t.ctaProducts}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center pt-16 border-t border-slate-200">
            {t.metrics.map(m => (
              <div key={m.label} className="group">
                <div className="text-4xl md:text-5xl font-light text-slate-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{m.value}</div>
                <div className="text-xs tracking-wider uppercase text-slate-500 font-medium">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Hero;